import React from 'react';
import BaseView from '../../components/CRUDView';
import {FarmersEndpoints, FBOEndpoints} from "../../common/ProxyEndpoints";
import Avatar from '../../components/ProfileAvatar';
import {Badge} from 'reactstrap';
import EntityImageDisplay from '../../components/EntityImageDisplay';
import Spinner from 'react-spinkit';
import Utils, {exportFilteredCSV} from "../../common/Utils";
import {Dialogs, Gender, GenderMap, Permissions} from "../../common/Constants";
import AppVariables from "../../common/AppVariables";
import FarmersCommon, {CustomFarmersFilter, FARMERS_QUERY_FILTERS, parseFarmerQueryFilters} from "./FarmersCommon";
import _ from 'lodash';
import HistoryLink from "../../common/HistoryLink";
import DialogCollapseSection from "../../components/DialogCollapseSection";
import StandardValue from "../../common/StandardValue";
import FarmSizeLabel, {FARM_SIZE_LABEL, FARM_SIZE_PROPERTY} from "../../common/FarmSizeLabel";
import {getDistricts, getLocationRecord, getWards, listProvinces} from "../../data/locations";
import convertUnit from 'convert-units';
import AppColors from "../../common/Colors";

const SearchProps = [
    'name' ,
    'phone' ,
    'email',
    'farm_location',
    "fbo.name",
    FARM_SIZE_PROPERTY
];

const CacheDependency = [
    'dashboard',
    'fbo'
];


/**
 * Provides access to creating farmers and assigning them to their FBO's
 */
class FarmersAdmin extends BaseView {

    constructor(props) {
        super(props,'farmers_admin');
    }

    fetchFBO(){

        return this.proxy.processRequest(FBOEndpoints.getUrl(), {
            fields: [ 'id', 'name'  ],
            include: [ 'user', 'media' ],
            method: 'get'
        });

    }

    getCustomFilterState(filters) {
        return parseFarmerQueryFilters(filters);
    }

    beforeShowDialog(dialog,state) {

        if(dialog === Dialogs.CreateOrEdit){

            const editItem = state ? state.editItem : null;
            const _provinceList = listProvinces();
            const _selectProvince = (editItem ? editItem.province : "" );
            const _districtList = _selectProvince ? getDistricts(_selectProvince) : [];
            const _selectDistrict = (editItem ? editItem.district : "" );
            const _wardList = (_selectProvince && _selectDistrict) ? getWards(_selectProvince,_selectDistrict) : [];

            return this.setStateSafe({
                _locationMode: state.editItem && (state.editItem.province && state.editItem.district && state.editItem.ward) ? 'select' : 'custom',
                _selectProvince,
                _selectDistrict,
                _provinceList,
                _districtList,
                _wardList
            }, () => super.beforeShowDialog(dialog));
        }

        return super.beforeShowDialog(dialog);
    }



    componentWillMount(){

        const me = this;
        this.setViewOptions({

            //  The message to show when there are no items in the view
            noRowsMessage: 'No farmers available yet!',

            //  The class icon for the card header
            pageIcon: 'fa fa-user',

            //  The title for the content frame
            pageTitle: 'Farmers',

            //  The total number of items per page
            maxItemsPerPage: AppVariables.MaxItemsPerPage,

            //  Defines the features of the view
            features: [ 'toolbox' , 'search' ],

            multiSelection: {

                //  enable multi-selection
                enable : true,
            },

            hooks:{

                //  Fetch Query Hooks
                preQueryHook: function () {

                    return me.fetchFBO().then(response => {

                        return {
                            fbo: response.getPayload()
                        };

                    });

                },

                postProcessorHook: (action,response) =>{
                    Utils.eraseSession(CacheDependency);
                }

            },

            //  defines (CRUD) actions enabled on the items
            actions:{

                //  create action options
                create:{
                    enabled: true,
                    text: 'Add Farmer',
                    successCreateMsg: 'Farmer created successfully!',
                    failCreateMsg : 'Failed creating farmer!',
                    editTitleMsg: 'Edit Farmer',
                    createTitleMsg: 'Add Farmer',
                    permissions:  [ Permissions.Farmers.Create ]
                },

                //  delete action options
                delete:{

                    enabled : false,

                    //  Disallows the deleting of many items ??
                    disallowMany: false,

                    successDeleteMsg: 'Deleted successfully!',
                    failDeleteMsg: 'Failed deleting',
                    text: 'Delete',
                    confirmTitle: 'Confirm Delete',
                    confirmMessage: 'Are you sure you want to delete farmer?',

                    //  The message to show when deleting many items
                    confirmManyMessage: 'Delete selected farmers?',
                    permissions:  [ Permissions.Farmers.Remove ]
                },

                //  edit options
                edit:{
                    enabled : true,
                    successEditMsg: 'Farmer edited successfully!',
                    failEditMsg: 'Failed editing farmer',
                    text: 'Edit Farmer',
                    permissions:  [ Permissions.Farmers.Update ]
                }

            },

            queryActions: {

                //  The endpoint for query actions
                endpoint: FarmersEndpoints.getUrl()
            },

            model: {
                include: [ 'user', 'fbo' , 'media' ]
            },

            filters: FARMERS_QUERY_FILTERS,

            renderCustomFilters: (props) => <CustomFarmersFilter includeFBO {...props}/>,

            search:{

                //
                placeholder: 'Search farmers...',

                //  the mode for searching
                //  immediate: responsive
                //  explicit: wait till user hits the search button
                mode: 'explicit',

                //  The minimum characters to begin search with
                minSearchChars: 2,

                //  Search properties
                properties: SearchProps

            },

            sort: {

                properties: [
                    {
                        label: "Date Added",
                        value: "date_created"
                    },
                    {
                        label: "Name",
                        value: "name"
                    },
                    {
                        label: "Phone",
                        value: "phone"
                    },
                    {
                        label: FARM_SIZE_LABEL,
                        value: FARM_SIZE_PROPERTY
                    }
                ]

            },

            //  Defines actions for toolbox
            toolBox: [

                //  ACTIONS
                {  text : 'Add Farmer'  , icon : 'fa fa-plus'  , invoke: () => { me.beginShowDialog({activeDialog: 0 }); }  , position: 'right' , permissions: [ Permissions.Farmers.Create ] },
                {  text : 'Refresh'  , icon : 'fa fa-refresh'  , invoke: () => me.refresh() , position: 'right' },
                {  text : 'Gender'  , icon : 'fa fa-male' , invoke: (index) => { me.updateQuery('sex',index) } , position: 'right' , group : 'filter' , items: GenderMap },
                {  text : null  , icon : 'fa fa-sort'  , invoke: (index) => { me.configureSort();  }, position: 'right' , group : 'filter'},
                {  text : ''  , icon : 'fa fa-gears' , invoke: (index) => {

                    switch (index){
                        case 0:
                            return exportFilteredCSV(FarmersEndpoints.exportCSV(), { component: me });
                        case 1:
                            me.toggleCustomFilterVisibility();
                            break;
                    }

                }, position: 'right' , group : 'more' ,
                    items: () => ([
                        <span><i className="fa fa-file-text"/> Export to CSV</span>,
                        <span><i className={me.isCustomFilterViewVisible() ? "fa fa-chevron-up" : "fa fa-chevron-down"}/> {me.isCustomFilterViewVisible() ? "Hide" : "Show"} Advanced Filters</span>
                    ]),
                    sticky: false,
                }
            ],

            //  Inputs used in editing and creating items
            inputs: () => FarmersCommon.getFarmerInputs(this.state.fbo,{
                locationMode: this.state._locationMode,
                onLocationModeChange: mode => {
                    this.setStateSafe({ _locationMode: mode })
                },
                provinces: this.state._provinceList,
                districts: this.state._districtList,
                wards: this.state._wardList,
                onProvinceChanged: value => {

                    const _districtList = value ? getDistricts(value) : [];
                    const _selectDistrict = _districtList.length > 0 ? _districtList[0].value : "";
                    const _wardList =  _selectDistrict ? getWards(value,_selectDistrict) : [];
                    const _selectWard = _wardList.length > 0 ?  _wardList[0].value : "";

                    this.setState({
                        _selectProvince: value,
                        _selectDistrict,
                        _districtList,
                        _wardList
                    },() => {

                        const renderer  = this.getDialogRenderer(Dialogs.CreateOrEdit);
                        renderer.setInputValueWithName('district', _selectDistrict );
                        renderer.setInputValueWithName('ward', _selectWard);

                    });

                },
                onDistrictChanged: value => {

                    const _wardList = getWards(this.state._selectProvince,value);
                    const _selectWard = _wardList[0].value;

                    this.setState({
                        _selectDistrict: value,
                        _wardList
                    }, () => {

                        const renderer  = this.getDialogRenderer(Dialogs.CreateOrEdit);
                        renderer.setInputValueWithName('ward', _selectWard);

                    });

                }
            }),

            // Describes the columns present in the table
            columns : [
                {   key: '__img' , label: ' ' , cell: (item,k) => <Avatar media={item.media} name={Utils.formatAvatarName(item.name)} size={32} /> , mobile: true },
                {   key: 'name', label: () => me.sortCol("Name","name") , format: 'trimmed' , mobile: true},
                {   key: 'fbo', label: 'FBO' , format: (fbo) => fbo ? fbo.name  :  <h6><Badge pill={true}>Unassigned</Badge></h6> },
                {   key: 'phone', label: () => me.sortCol("Phone","phone"), format: 'trimmed', mobile: true },
                {   key: FARM_SIZE_PROPERTY , label: () => me.sortCol("Farm Size",FARM_SIZE_PROPERTY), cell: (item,k) => <StandardValue value={item[k]}><FarmSizeLabel value={item[k]}/></StandardValue>}
            ],

            //  Describes details to show when clicked
            detailsDisplay: {

                //  enable or disable
                enabled: true,

                renderTitle: (item) => {
                    return 'Farmer Info';
                },

                customRender: (item) => {

                    const me = this;
                    return <div>
                        <div className="text-center">
                            <h5>Profile Image</h5>
                            <EntityImageDisplay
                                entityName="farmer"
                                entityId={item.id}
                                crudView={me}
                                onRemove={(media) => {
                                    me.updateItemMedia(item,media);
                                }}
                                onCompleteUpload={(media) => {
                                    me.updateItemMedia(item,media);
                                    return false;
                                }}
                                media={item.media} />
                        </div>

                        <div>

                            <div className="clearfix">
                                <div className="pull-left">
                                    <h5>Info</h5>
                                </div>
                                <div className="pull-right">
                                    {me.state.isBusy && <Spinner name="ball-scale-multiple" color="black" style={{marginRight: '30px'}}/>}
                                </div>
                            </div>

                            <hr/>

                            <div className="row">
                                <div className="col-md-6">
                                    <dl>
                                        <dt>Name</dt>
                                        <dl>{item.name}</dl>

                                        <dt>Phone</dt>
                                        <dl>{item.phone}</dl>

                                        <dt>Sex</dt>
                                        <dl>{(!_.isNil(item.sex)) ? GenderMap[item.sex] : "N/A" }</dl>

                                        <dt>Email</dt>
                                        <dl>{Utils.standardValue(item.email)}</dl>

                                        <dt>Date Of Birth</dt>
                                        <dl>
                                            <StandardValue value={item.date_of_birth}>
                                             {Utils.formatDateShort(item.date_of_birth)}
                                            </StandardValue>
                                        </dl>

                                        <dt>Date Added</dt>
                                        <dl>{Utils.formatDateStandard(item.date_created)}</dl>


                                    </dl>
                                </div>
                                <div className="col-md-6">
                                    <dl>

                                        <dt>Farm Size</dt>
                                        <dl>

                                            <StandardValue value={item[FARM_SIZE_PROPERTY]}>
                                                <FarmSizeLabel item={item}/>
                                            </StandardValue>

                                        </dl>

                                        <dt>Location</dt>
                                        <dl style={{
                                            color: !_.isNil(item.farm_location_lat) && !_.isNil(item.farm_location_long) ? AppColors.PageBackgroundColor : undefined,
                                            cursor: "pointer"
                                        }} onClick={() => {

                                            if(!_.isNil(item.farm_location_lat) && !_.isNil(item.farm_location_long))
                                                window.open("https://maps.google.com?q=" + item.farm_location_lat + "," + item.farm_location_long );

                                        }}>{item.farm_location ? item.farm_location :  (item.province && item.district && item.ward ? [item.province,item.district,item.ward].join(',') : '' ) || "N/A" }</dl>

                                        <dt>FBO</dt>
                                        <dl>{item.fbo ? <HistoryLink url={`/fbo?filter_id=${item.fbo.id}&q=${item.fbo.name}`} tooltip="Click to see FBO" className="btn-link">{item.fbo.name}</HistoryLink> : "Unassigned"}</dl>

                                        <dt>Crop(s)</dt>
                                        <dl>{item.crop ? item.crop.split(",").map( (m,k) => <Badge key={k} pill={true} style={{marginRight: 5}}>{m}</Badge>) : "N/A" }</dl>

                                        <dt>User Account Status</dt>
                                        <dl><h6>{item.user ? <Badge pill color={"success"}>Registered <i className="fa fa-check"/></Badge> : <Badge pill color={"secondary"}>Not Registered</Badge>}</h6></dl>

                                    </dl>
                                </div>
                            </div>

                        </div>

                        {item.user && <DialogCollapseSection title={<h5>Account Info</h5>}>
                            <hr/>
                            <div className="row">
                                <div className="col-md-6">

                                    <dl>

                                        <dt>Username</dt>
                                        <dl>{item.user.name}</dl>

                                    </dl>
                                </div>
                                <div className="col-md-6">
                                    <dl>

                                        <dt>Time Registered</dt>
                                        <dl>{Utils.formatDateStandard(item.user.date_created)}</dl>

                                    </dl>
                                </div>
                            </div>
                        </DialogCollapseSection>}

                    </div>
                }
            },

            //  Processors
            processors :{

                //  create new item
                createItem: (proxy , payload) => {

                    let promise;

                    const { _locationMode } = this.state;

                    if(_locationMode === 'select'){

                        const loc = getLocationRecord(payload);
                        if(loc){
                            payload.farm_location = [payload.province,payload.district,payload.ward].join(",");
                            payload.farm_location_lat = loc.lat;
                            payload.farm_location_long = loc.lng;
                        }

                    }
                    else if(_locationMode === 'custom'){
                        payload.province = null;
                        payload.district = null;
                        payload.ward = null;
                    }

                    //  select only required properties
                    if(payload.fbo && payload.fbo > 0){

                        const fboId = payload.fbo;
                        delete payload['fbo'];
                        promise =  proxy.processRequest(FarmersEndpoints.create(fboId,payload));
                    }
                    else{
                        delete payload['fbo'];
                        promise = proxy.processRequest(FarmersEndpoints.createFarmerNoFBO(payload));
                    }

                    return promise;
                },

                //  on edit item
                onEditItem: (proxy,item, formData, affected ) => {

                    const { _locationMode } = this.state;

                    //  fix form data
                    affected.fbo_id = affected.fbo ? affected.fbo : null;

                    if(affected.hasOwnProperty(FARM_SIZE_PROPERTY)) {

                        let _value = affected[FARM_SIZE_PROPERTY] || null;
                        affected[FARM_SIZE_PROPERTY] = _value;

                        if(FARM_SIZE_PROPERTY === 'acres'){
                            affected.hectares = convertUnit(_value).from('ac').to('ha');
                        }
                        else if(FARM_SIZE_PROPERTY === 'hectares'){
                            affected.acres = convertUnit(_value).from('ha').to('ac');
                        }
                    }

                    delete affected['fbo'];

                    if(_locationMode === 'select'){

                        //  location affected
                        if(affected.province || affected.district || affected.ward){

                            const loc = getLocationRecord(formData);
                            if(loc){
                                affected.farm_location = [formData.province,formData.district,formData.ward].join(",");
                                affected.farm_location_lat = loc.lat;
                                affected.farm_location_long = loc.lng;
                            }

                        }

                    }
                    else if(_locationMode === 'custom'){
                        affected.ward = null;
                        affected.province = null;
                        affected.district = null;
                    }



                    //
                    if(Object.keys(affected).find(x => affected[x] !== undefined)) {
                        affected.last_updated = item.last_updated;
                        return proxy.processRequest(FarmersEndpoints.update(item.id, affected));
                    }
                    else{
                        return Promise.resolve();
                    }
                },

                //  on delete item
                onDeleteItem: ( proxy,item) => {
                    return proxy.processRequest(FarmersEndpoints.deleteFarmer(item.id));
                },

                //  called to delete many items
                deleteMany: (proxy,items) => {
                    return proxy.processRequest(FarmersEndpoints.deleteFarmer(items.map((f) => f.id)));
                }

            }
        });

    }

}

export default FarmersAdmin;



// WEBPACK FOOTER //
// ./src/views/Farmers/FarmersAdmin.js